import { DecimalString } from '../../../api/types/analytics';
import { REGION_DISPLAY_NAMES, RegionCode } from '../../../config';

export const currentLocale = () => (localStorage.getItem('lang') === 'en' ? 'en-US' : 'uk-UA');

/**
 * Format a decimal string as money. Intl accepts numeric strings in modern
 * engines; formatting only — no arithmetic happens here.
 */
export const formatMoney = (value: DecimalString | null | undefined, currency = 'USD', compact = false) => {
  // null = unknown (e.g. region without a USD rate) — never render it as 0.
  if (value === null) return '—';
  const v = value ?? '0';
  try {
    return new Intl.NumberFormat(currentLocale(), {
      style: 'currency',
      currency,
      maximumFractionDigits: compact ? 0 : 2,
      minimumFractionDigits: compact ? 0 : 2,
    }).format(v as unknown as number);
  } catch {
    return `${v} ${currency}`;
  }
};

export const formatInt = (value: number | null | undefined) =>
  new Intl.NumberFormat(currentLocale()).format(value || 0);

export const formatPercent = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat(currentLocale(), {
        style: 'percent',
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
      }).format(value / 100);

export const formatMonth = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Intl.DateTimeFormat(currentLocale(), { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, 1)),
  );
};

export const formatDateTime = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(currentLocale(), { dateStyle: 'medium', timeStyle: 'short' }).format(d);
};

/** Display text or a dash for null/empty values. */
export const orDash = (value: string | null | undefined) => (value === null || value === undefined || value === '' ? '—' : value);

export const regionShort = (region: string) => REGION_DISPLAY_NAMES[region as RegionCode]?.short || region;

export const regionName = (region: string) => {
  const r = REGION_DISPLAY_NAMES[region as RegionCode];
  if (!r) return region;
  return localStorage.getItem('lang') === 'en' ? r.en : r.uk;
};
